/**
 * Rechenkern der Bedienoberflaeche zum Aufteilungsschluessel: Prozenttext des
 * Formulars <-> Basispunkte des Schluessels.
 *
 * Bewusst OHNE React, DOM und Datenzugriff und deshalb in einer eigenen Datei.
 * Das Projekt hat keine jsdom-Testumgebung; was in einer Komponente steht, ist
 * nicht testbar. Hier liegt darum alles, worin ein Rechenfehler Geld kostet —
 * SplitKeyEditor.tsx bleibt die duenne Huelle darum.
 *
 * Gerechnet wird ausschliesslich in ganzen BASISPUNKTEN (100,00 % = 10000).
 * Ein Drittel ist als Prozent-Gleitkommazahl nicht darstellbar; drei Drittel
 * muessen sich aber exakt zu 100 % addieren. In Basispunkten ist die Pruefsumme
 * eine Ganzzahlgleichung (3334 + 3333 + 3333 = 10000) statt eines Vergleichs
 * mit Toleranz — dieselbe Entscheidung wie im Rechenkern (projectSplit.ts).
 *
 * Getippt wird trotzdem in Prozent mit deutschem Komma, weil der Betrieb in
 * Prozent denkt und verhandelt. Die Umrechnung passiert an genau EINER Stelle
 * (parseSharePercent / formatSharePercent) und ohne Gleitkommazahl: der Text
 * wird an seinem Komma zerlegt und ziffernweise zusammengesetzt. Ueber
 * parseFloat("33.33") * 100 kaeme 3332.9999999999995 heraus — mit Runden zwar
 * richtig, aber mit einer Rundung mehr im Weg, als noetig ist.
 */
import {
    ProjectSplit,
    ProjectSplitParticipant,
    SHARE_FULL_BP,
    shareBpFor,
} from '../../services/projectSplit';

// ═══════════════════════════════════════════════════════════════════════════
// Prozenttext <-> Basispunkte
// ═══════════════════════════════════════════════════════════════════════════

/**
 * "33,33" -> 3333. Ungueltige Eingabe -> null.
 *
 * Erlaubt sind 0 bis 100 mit hoechstens ZWEI Nachkommastellen, Komma oder
 * Punkt als Trenner, ein angehaengtes Prozentzeichen und Leerzeichen. Mehr
 * Nachkommastellen werden ABGELEHNT und nicht gerundet: 33,333 % ist im
 * Basispunkt-Raster nicht darstellbar, und eine stillschweigende Rundung
 * veraenderte die Zahl, die der Nutzer gerade eingetippt hat. Wer 33,333 meint,
 * muss sich entscheiden — die Pruefsumme zeigt ihm sofort, wohin der fehlende
 * Basispunkt gehoert.
 *
 * Zerlegt wird der TEXT und nicht das Ergebnis einer Gleitkommazahl: aus "33"
 * und "33" wird 33 * 100 + 33. Damit gibt es im ganzen Pfad keine Stelle, an
 * der ein Hundertstel verloren gehen koennte.
 */
export const parseSharePercent = (input: string): number | null => {
    const raw = (input ?? '').replace(/\s/g, '').replace(/%/g, '');
    if (!raw) return null;

    const normalized = raw.replace(',', '.');
    if (!/^\d{1,3}(\.\d{0,2})?$/.test(normalized)) return null;

    const [whole, frac = ''] = normalized.split('.');
    // Auf zwei Stellen auffuellen: "5" nach dem Komma sind fuenf Zehntel
    // Prozent (50 Basispunkte) und nicht fuenf Hundertstel.
    const bp = Number(whole) * 100 + Number((frac + '00').slice(0, 2));
    if (bp > SHARE_FULL_BP) return null;
    return bp;
};

/**
 * 3333 -> "33,33". Ohne Prozentzeichen — das steht in der Spaltenueberschrift.
 *
 * Ganzzahlig zusammengesetzt, aus demselben Grund wie beim Lesen. Immer zwei
 * Nachkommastellen, damit die Spalte untereinander buendig steht und "50" nicht
 * neben "33,33" wie eine andere Groesse aussieht.
 */
export const formatSharePercent = (bp: number): string => {
    const safe = Math.max(0, Math.round(bp || 0));
    return `${Math.floor(safe / 100)},${String(safe % 100).padStart(2, '0')}`;
};

/**
 * 10000 Basispunkte auf `count` Teilnehmer, nach dem Verfahren des groessten
 * Restes.
 *
 * Die Summe ist EXAKT 10000 — das ist der ganze Zweck. Bei drei Teilnehmern
 * kommen 3334 / 3333 / 3333 heraus. Naiv gerundet ergaeben sich dreimal 3333
 * und damit 99,99 %; das fehlende Hundertstel Prozent faellt an keinem Beleg
 * auf und bleibt dauerhaft unberechnet.
 *
 * Der Rest geht an die VORDEREN Plaetze, und die Reihenfolge der Teilnehmer ist
 * stabil (Abschnitt, dann Auftraggeber, wie sie der Service liefert). Ohne
 * feste Ordnung ergaebe derselbe Knopfdruck beim naechsten Mal eine andere
 * Verteilung.
 */
export const distributeEvenly = (count: number): number[] => {
    if (count <= 0) return [];
    const base = Math.floor(SHARE_FULL_BP / count);
    const rest = SHARE_FULL_BP - base * count;
    return Array.from({ length: count }, (_, i) => base + (i < rest ? 1 : 0));
};

// ═══════════════════════════════════════════════════════════════════════════
// Entwurf -> Schluessel
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Was im Formular steht, solange getippt wird: ROHTEXT.
 *
 * Bewusst nicht die geparste Zahl. Wer "3" tippt, um auf "33,33" zu kommen,
 * darf nicht dabei zusehen, wie das Feld unter seinen Fingern zu "3,00" wird —
 * und ein halb getipptes "33," ist voruebergehend unlesbar, ohne dass deshalb
 * etwas verloren sein darf.
 */
export interface SplitDraftParticipant {
    /**
     * Die Identitaet der Zeile (project_splits.id). Fuer eine neue Zeile im
     * Formular vergeben, nicht erst beim Speichern: die Matrix schluesselt ihre
     * Zellen darueber, und bei zwei Abschnitten desselben Auftraggebers waere
     * die Kundenkennung als Schluessel mehrdeutig — beide Spalten trueg dann
     * dieselben Zahlen.
     */
    partyId: string;
    customerId: string;
    /** Der Abschnitt ("Haus 3"). Leer = kein Abschnitt, der Bestandsfall. */
    label: string;
    /** Grundquote als Text. Leer bedeutet 0,00 % — der Teilnehmer traegt nichts von sich aus. */
    defaultInput: string;
    /**
     * Ausnahmen je LV-Position (serviceId -> Text).
     *
     * Ein leerer oder fehlender Eintrag ist KEINE Null, sondern "erbt die
     * Grundquote". Der Unterschied ist tragend: 0 nimmt den Auftraggeber aus
     * der Position heraus, Erben laesst ihn mit seinem Regelanteil darin.
     */
    overrideInputs: Record<string, string>;
}

/** Eine Zelle, deren Text sich nicht als Prozentwert lesen laesst. */
export interface UnreadableCell {
    /** Die SPALTE — der Teilnehmer, nicht der Auftraggeber. */
    partyId: string;
    customerId: string;
    /** Fehlt bei der Grundquote. */
    serviceId?: string;
}

export interface ParsedSplitDraft {
    split: ProjectSplit;
    unreadable: UnreadableCell[];
}

/**
 * Aus dem Rohtext des Formulars den Schluessel bauen, den der Rechenkern und
 * die Datenschicht erwarten.
 *
 * Unlesbare Zellen werden GEMELDET und nicht stillschweigend als 0 gewertet.
 * Als 0 gelesen sieht die Zeile schlicht nach einem zu kleinen Anteil aus; der
 * Nutzer sucht dann den Fehler in der falschen Zeile, waehrend er in Wahrheit
 * einen Tippfehler in genau dieser Zelle stehen hat. Der Schluessel traegt an
 * so einer Stelle einen Platzhalter (Grundquote 0, Ausnahme faellt weg), damit
 * die Matrix ueberhaupt weiterrechnen kann — er darf aber weder geprueft noch
 * gespeichert werden, solange `unreadable` etwas enthaelt. Wer diese Liste
 * ignoriert, arbeitet mit einer Zahl, die niemand eingegeben hat.
 *
 * Ausnahmen mit leerem Text fallen HERAUS statt als 0 einzugehen — das ist die
 * Art, wie im Formular eine Ausnahme wieder entfernt wird (Feld leeren = erbt
 * wieder). Und `overrides` bleibt ganz weg, wenn keine Ausnahme uebrig ist:
 * `shareBpFor` fragt mit `!== undefined` ab, und ein Vergleich zweier
 * Schluessel soll nicht an einem leeren Objekt scheitern, das nichts bedeutet.
 */
export const parseSplitDraft = (
    projectId: string,
    draft: readonly SplitDraftParticipant[],
): ParsedSplitDraft => {
    const unreadable: UnreadableCell[] = [];
    const participants: ProjectSplitParticipant[] = [];

    for (const row of draft) {
        const defaultText = (row.defaultInput ?? '').trim();
        const parsedDefault = defaultText ? parseSharePercent(defaultText) : 0;
        if (parsedDefault == null) {
            unreadable.push({ partyId: row.partyId, customerId: row.customerId });
        }

        const overrides: Record<string, number> = {};
        for (const [serviceId, text] of Object.entries(row.overrideInputs ?? {})) {
            const value = (text ?? '').trim();
            if (!value) continue;
            const parsed = parseSharePercent(value);
            if (parsed == null) {
                unreadable.push({ partyId: row.partyId, customerId: row.customerId, serviceId });
                continue;
            }
            overrides[serviceId] = parsed;
        }

        const participant: ProjectSplitParticipant = {
            partyId: row.partyId,
            customerId: row.customerId,
            defaultShareBp: parsedDefault ?? 0,
        };
        const label = (row.label ?? '').trim();
        if (label) participant.label = label;
        if (Object.keys(overrides).length > 0) participant.overrides = overrides;
        participants.push(participant);
    }

    return { split: { projectId, participants }, unreadable };
};

/**
 * Pruefsumme EINER Zeile in Basispunkten.
 *
 * Ohne `serviceId` ist es die Zeile der Grundquoten, mit `serviceId` die einer
 * LV-Position — dieselbe Rechnung, weil eine Ausnahme nichts anderes ist als
 * eine andere Belegung desselben Anteils.
 */
export const rowSumBp = (
    participants: readonly ProjectSplitParticipant[],
    serviceId?: string,
): number => participants.reduce((acc, p) => acc + shareBpFor(p, serviceId), 0);

/** Den Schluessel aus der Datenbank in Formulartext ueberfuehren. */
export const draftFromSplit = (split: ProjectSplit | null): SplitDraftParticipant[] =>
    (split?.participants ?? []).map((p) => ({
        partyId: p.partyId,
        customerId: p.customerId,
        label: p.label ?? '',
        defaultInput: formatSharePercent(p.defaultShareBp),
        overrideInputs: Object.fromEntries(
            Object.entries(p.overrides ?? {}).map(([serviceId, bp]) => [
                serviceId,
                formatSharePercent(bp),
            ]),
        ),
    }));
